"use client";
import { useMutation } from "@apollo/client/react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RequestOptions } from "mixpanel-browser";
import Cookies from "js-cookie";
import { COOKIE_KEYS } from "@portal/app/_common/lib/headers";
import {
  deserializeGoogleAdsAttribution,
  normalizeGoogleAdsCapture,
} from "@portal/app/_common/lib/googleAdsAttribution";
import * as Sentry from "@sentry/nextjs";

import { TokenInfo, decodeJwt } from "@portal/app/_common/lib/token";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { accessTokenVar } from "@portal/app/_components/apolloUtils";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { getJornayaLeadId, useJornaya } from "@portal/app/_components/hooks/useJornaya";
import { TrustedFormScript } from "@portal/app/_components/scripts/TrustedFormScript";
import { FormFunnelTrackingContext } from "./hooks/useFormFunnelTracking";
import { useReactiveVar } from "@apollo/client/react";
import { EVERFLOW_OFFER_ID } from "@portal/app/_common/types/everflow";
import { logger } from "@portal/app/_common/lib/logger";
import { isValidFbcCookie, isValidFbpCookie } from "@portal/app/_common/lib/trackingValidation";
import type { FunnelExperimentAttribution } from "@portal/app/_server/services/formFunnelExperiment/types";
import { gql } from "graphql-tag";
import { AttemptAddDreamSearchClickEventDocument } from "./generated";
import EverflowSDK from "@everflow/everflow-sdk";
import { useFlowStepTracking } from "@portal/app/_components/hooks/useFlowStepTracking";
import type { HostedFlowStep } from "@portal/app/_server/services/formFunnel/payloadTypes";
import {
  describeHostedFunnelStep,
  type HostedFlowTrackingPhase,
  type HostedFlowTrackingStep,
} from "./utils/flowStepDescriptors";

/** Tracks which malformed cookie values have already been reported to Sentry this session. */
const reportedMalformedCookies = new Set<string>();

/** Validates a Facebook cookie and logs to Sentry (once per value) if malformed. */
function getValidatedFbCookie(
  cookieName: string,
  validator: (value: string) => boolean,
  funnelSlug: string
): string | null {
  const raw = Cookies.get(cookieName) || null;
  if (raw && !validator(raw)) {
    if (!reportedMalformedCookies.has(raw)) {
      reportedMalformedCookies.add(raw);
      Sentry.captureMessage(`Malformed ${cookieName} cookie detected`, {
        level: "warning",
        tags: { feature: "form-funnel", scenario: "tracking-validation" },
        extra: { rawValue: raw, cookieName, funnelSlug },
      });
    }
    return null;
  }
  return raw;
}

void gql`
  mutation attemptAddDreamSearchClickEvent($data: DreamSearchClickEventCreateInput!) {
    attemptAddDreamSearchClickEvent(data: $data) {
      id
      customerId
    }
  }
`;

interface EverflowAffiliateParams {
  offerId: string | null;
  affiliateId: string | null;
  sub1: string | null;
  sub2: string | null;
  sub3: string | null;
  sub4: string | null;
  sub5: string | null;
  sub6: string | null;
  sub7: string | null;
  sub8: string | null;
  uid: string | null;
  sourceId: string | null;
}

interface TrackingConfig {
  programId?: string | null;
  funnelSlug: string;
  funnelName: string;
  enableTrustedForm?: boolean;
  facebookPixelId?: string | null;
  funnelExperiments: FunnelExperimentAttribution[];
  isFreshAssignment: boolean;
  experimentName?: string;
  createMarketplaceEventOnMount: boolean;
  /** CMS draft previews do not contribute production progression events. */
  isPreview?: boolean;
  questionnaireStepCount: number;
  hostedFlowSteps?: readonly HostedFlowStep[];
}

interface FormFunnelTrackingProviderProps {
  children: React.ReactNode;
  trackingConfig: TrackingConfig;
}

export const FormFunnelTrackingProvider = ({
  children,
  trackingConfig,
}: FormFunnelTrackingProviderProps) => {
  const accessToken = useReactiveVar(accessTokenVar);
  const mixpanel = useMixpanel();
  const { jornayaLeadId } = useJornaya();
  const [trustedFormCertId, setTrustedFormCertId] = useState<string | null>(null);
  // Note: no React state for Everflow transaction ID. It's written to
  // `document.cookie` (first-party on dreambound.com) by the Everflow SDK's
  // `_persist` synchronously on successful `click()`. Any read at submit
  // time goes through `EverflowSDK.getTransactionId` which is just a cookie
  // lookup — caching it in state was redundant. The 30s capture-timeout
  // telemetry that used to live here moved into EverflowScript alongside
  // the other SDK outcome telemetry.
  const [marketplaceEventId, setMarketplaceEventId] = useState<string | null>(null);
  const [trackingInitialized, setTrackingInitialized] = useState(false);
  const jornayaRef = useRef(jornayaLeadId);
  jornayaRef.current = jornayaLeadId;
  const initInFlightRef = useRef(false);
  const assignmentFiredRef = useRef(false);
  const [facebookClickId, setFacebookClickId] = useState<string | null>(null);
  const [facebookBrowserId, setFacebookBrowserId] = useState<string | null>(null);
  const [affiliateParams, setAffiliateParams] = useState<EverflowAffiliateParams>({
    offerId: null,
    affiliateId: null,
    sub1: null,
    sub2: null,
    sub3: null,
    sub4: null,
    sub5: null,
    sub6: null,
    sub7: null,
    sub8: null,
    uid: null,
    sourceId: null,
  });

  const [attemptAddEvent] = useMutation(AttemptAddDreamSearchClickEventDocument);

  const { customerId, activeRole } = useMemo(
    () => decodeJwt<TokenInfo>(accessToken) ?? ({} as TokenInfo),
    [accessToken]
  );
  const isAdmin = activeRole === "ADMIN";
  const trackFlowStep = useFlowStepTracking(
    trackingConfig.funnelSlug,
    !isAdmin && !trackingConfig.isPreview
  );
  const trackHostedFlowStep = useCallback(
    (step: HostedFlowTrackingStep, phase?: HostedFlowTrackingPhase) => {
      const descriptor = describeHostedFunnelStep({
        questionnaireStepCount: trackingConfig.questionnaireStepCount,
        configuredSteps: trackingConfig.hostedFlowSteps,
        step,
        phase,
      });
      if (descriptor) trackFlowStep(descriptor);
    },
    [trackingConfig.questionnaireStepCount, trackingConfig.hostedFlowSteps, trackFlowStep]
  );

  // Capture URL parameters on mount (for Everflow affiliate tracking)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const urlParams = new URLSearchParams(window.location.search);

    setAffiliateParams({
      offerId: urlParams.get("oid"),
      affiliateId: urlParams.get("affid"),
      sub1: urlParams.get("sub1"),
      sub2: urlParams.get("sub2"),
      sub3: urlParams.get("sub3"),
      sub4: urlParams.get("sub4"),
      sub5: urlParams.get("sub5"),
      sub6: urlParams.get("sub6"),
      sub7: urlParams.get("sub7"),
      sub8: urlParams.get("sub8"),
      uid: urlParams.get("uid"),
      sourceId: urlParams.get("source_id"),
    });
  }, []);

  // Poll for TrustedForm Certificate ID
  useEffect(() => {
    if (!trackingConfig.enableTrustedForm) return;

    let captured = false;
    const TRUSTEDFORM_TIMEOUT_MS = 30000;

    const interval = setInterval(() => {
      // Check window.trustedForm.id first
      if (window.trustedForm?.id) {
        setTrustedFormCertId(window.trustedForm.id);
        captured = true;
        clearInterval(interval);
        return;
      }

      // Fallback: check hidden input field
      const tfField = document.querySelector(
        'input[name="xxTrustedFormCertUrl"]'
      ) as HTMLInputElement;
      if (tfField?.value) {
        setTrustedFormCertId(tfField.value);
        captured = true;
        clearInterval(interval);
      }
    }, 500);

    // Stop polling after 30 seconds and log timeout to Sentry
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!captured) {
        Sentry.captureMessage("TrustedForm certificate polling timeout", {
          level: "warning",
          tags: { feature: "form-funnel", scenario: "compliance-timeout" },
          extra: {
            trackingType: "trustedform",
            timeoutMs: TRUSTEDFORM_TIMEOUT_MS,
            programId: trackingConfig.programId,
          },
        });
      }
    }, TRUSTEDFORM_TIMEOUT_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [trackingConfig.enableTrustedForm, trackingConfig.programId]);

  // Everflow transaction ID is read directly from the cookie at submit time
  // (see `getTrackingIds`). The module-level `scheduleCaptureTimeoutWarning`
  // in EverflowScript emits the corresponding 30s "capture timeout" Sentry
  // warning alongside the rest of the SDK outcome telemetry — this provider
  // no longer duplicates it.

  // Poll for Facebook Pixel cookies (_fbc and _fbp)
  useEffect(() => {
    if (!trackingConfig.facebookPixelId) return;

    let captured = false;
    const FB_COOKIE_TIMEOUT_MS = 30000;

    const interval = setInterval(() => {
      const fbc = Cookies.get("_fbc");
      const fbp = Cookies.get("_fbp");

      // Validate before storing — reject malformed values (e.g. Everflow macro leak)
      if (fbc && isValidFbcCookie(fbc)) {
        setFacebookClickId(fbc);
      }
      // Stop polling once _fbp is found (_fbc only exists for Facebook ad clicks)
      if (fbp && isValidFbpCookie(fbp)) {
        setFacebookBrowserId(fbp);
        captured = true;
        clearInterval(interval);
      }
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!captured) {
        Sentry.captureMessage("Facebook Pixel cookie polling timeout", {
          level: "warning",
          tags: { feature: "form-funnel", scenario: "tracking-timeout" },
          extra: {
            trackingType: "facebook-pixel",
            timeoutMs: FB_COOKIE_TIMEOUT_MS,
            funnelSlug: trackingConfig.funnelSlug,
          },
        });
      }
    }, FB_COOKIE_TIMEOUT_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [trackingConfig.facebookPixelId, trackingConfig.funnelSlug]);

  // Hosted and hybrid program-specific funnels keep their page-view event.
  // Pure clickouts create it only after the suppression gate at submission.
  useEffect(() => {
    if (trackingInitialized || initInFlightRef.current) return;

    if (!trackingConfig.programId || isAdmin || !trackingConfig.createMarketplaceEventOnMount) {
      setTrackingInitialized(true);
      return;
    }

    const initTracking = async () => {
      try {
        const currentJornayaLeadId = jornayaRef.current ?? getJornayaLeadId();
        const response = await attemptAddEvent({
          variables: {
            data: {
              programId: trackingConfig.programId!,
              customerId: customerId || null,
              jornayaLeadId: currentJornayaLeadId || null,
              everflowTransactionId: EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) || null,
              gaClientId: Cookies.get("_ga") || null,
              ...normalizeGoogleAdsCapture(
                deserializeGoogleAdsAttribution(Cookies.get(COOKIE_KEYS.GOOGLE_ADS_ATTRIBUTION))
              ),
            },
          },
        });

        if (response.data?.attemptAddDreamSearchClickEvent) {
          setMarketplaceEventId(response.data.attemptAddDreamSearchClickEvent.id);
        }
      } catch (error) {
        logger.error(error);
      } finally {
        setTrackingInitialized(true);
      }
    };

    initInFlightRef.current = true;
    const timer = setTimeout(() => void initTracking(), 1000);

    return () => {
      clearTimeout(timer);
      initInFlightRef.current = false;
    };
  }, [
    attemptAddEvent,
    customerId,
    isAdmin,
    trackingConfig.createMarketplaceEventOnMount,
    trackingConfig.programId,
    trackingInitialized,
  ]);

  // Register funnelExperiments as a Mixpanel super property and fire the
  // one-shot assignment event when the visitor is freshly bucketed.
  useEffect(() => {
    // Always register the super property — empty array is meaningful too
    // (lets Mixpanel queries distinguish "no test running" from "test running, in control").
    mixpanel.provider.register({ funnelExperiments: trackingConfig.funnelExperiments });

    // Fire assignment event once per visitor per experiment exposure
    if (
      trackingConfig.isFreshAssignment &&
      !assignmentFiredRef.current &&
      trackingConfig.funnelExperiments.length > 0
    ) {
      assignmentFiredRef.current = true;
      const attribution = trackingConfig.funnelExperiments[0];
      void mixpanel.track("Form Funnel - Variant Assigned", {
        experimentKey: attribution.key,
        experimentName: trackingConfig.experimentName,
        variantId: attribution.variantId,
        variantName: attribution.variantName,
        hostSlug: attribution.hostSlug,
        funnelSlug: trackingConfig.funnelSlug,
        funnelName: trackingConfig.funnelName,
      });
    }
  }, [
    trackingConfig.funnelExperiments,
    trackingConfig.isFreshAssignment,
    trackingConfig.experimentName,
    trackingConfig.funnelSlug,
    trackingConfig.funnelName,
    mixpanel,
  ]);

  const trackStepComplete = useCallback(
    (stepName: string, stepIndex: number) => {
      void mixpanel.track("Form Funnel - Step Completed", {
        stepName,
        stepIndex,
        funnelSlug: trackingConfig.funnelSlug,
        funnelName: trackingConfig.funnelName,
        ...(trackingConfig.programId && { programId: trackingConfig.programId }),
        marketplaceEventId,
        funnelExperiments: trackingConfig.funnelExperiments,
      });
    },
    [
      mixpanel,
      trackingConfig.funnelSlug,
      trackingConfig.funnelName,
      trackingConfig.programId,
      trackingConfig.funnelExperiments,
      marketplaceEventId,
    ]
  );

  const trackFormSubmit = useCallback(() => {
    void mixpanel.track("Form Funnel - Lead Submitted", {
      funnelSlug: trackingConfig.funnelSlug,
      funnelName: trackingConfig.funnelName,
      ...(trackingConfig.programId && { programId: trackingConfig.programId }),
      marketplaceEventId,
      funnelExperiments: trackingConfig.funnelExperiments,
    });
  }, [
    mixpanel,
    trackingConfig.funnelSlug,
    trackingConfig.funnelName,
    trackingConfig.programId,
    trackingConfig.funnelExperiments,
    marketplaceEventId,
  ]);

  const getTrackingIds = useCallback(async () => {
    // Read immediately here; the matching page owns its bounded Jornaya wait.
    const currentJornaya = jornayaLeadId ?? getJornayaLeadId();

    // Get current TrustedForm cert (may have updated since last poll)
    let currentTrustedForm = trustedFormCertId;
    if (!currentTrustedForm && typeof window !== "undefined") {
      if (window.trustedForm?.id) {
        currentTrustedForm = window.trustedForm.id;
      } else {
        const tfField = document.querySelector(
          'input[name="xxTrustedFormCertUrl"]'
        ) as HTMLInputElement;
        if (tfField?.value) {
          currentTrustedForm = tfField.value;
        }
      }
    }

    // Get current Everflow transaction ID. Reads directly from the first-party
    // cookie via the SDK (document.cookie → ef_tid_c_o_<offer>). No local
    // caching — the cookie is the source of truth, and the SDK writes it
    // synchronously on successful click().
    const currentEverflow = EverflowSDK.getTransactionId(EVERFLOW_OFFER_ID) || null;

    return {
      // Core IDs
      customerId: customerId || null,
      marketplaceEventId,

      // Compliance IDs
      jornayaLeadId: currentJornaya,
      trustedFormCertId: currentTrustedForm,
      everflowTransactionId: currentEverflow,

      // Facebook cookies — use polled values, fallback to direct read at submission time
      facebookClickId:
        facebookClickId ??
        getValidatedFbCookie("_fbc", isValidFbcCookie, trackingConfig.funnelSlug),
      facebookBrowserId:
        facebookBrowserId ??
        getValidatedFbCookie("_fbp", isValidFbpCookie, trackingConfig.funnelSlug),

      // Google Analytics
      gaClientId: Cookies.get("_ga") || null,
      ...normalizeGoogleAdsCapture(
        deserializeGoogleAdsAttribution(Cookies.get(COOKIE_KEYS.GOOGLE_ADS_ATTRIBUTION))
      ),

      // Everflow affiliate params
      everflowOfferId: affiliateParams.offerId,
      everflowAffiliateId: affiliateParams.affiliateId,
      everflowSub1: affiliateParams.sub1,
      everflowSub2: affiliateParams.sub2,
      everflowSub3: affiliateParams.sub3,
      everflowSub4: affiliateParams.sub4,
      everflowSub5: affiliateParams.sub5,
      everflowSub6: affiliateParams.sub6,
      everflowSub7: affiliateParams.sub7,
      everflowSub8: affiliateParams.sub8,
      everflowUid: affiliateParams.uid,
      everflowSourceId: affiliateParams.sourceId,
    };
  }, [
    customerId,
    facebookBrowserId,
    facebookClickId,
    jornayaLeadId,
    trustedFormCertId,
    marketplaceEventId,
    affiliateParams,
    trackingConfig.funnelSlug,
  ]);

  const trackEvent = useCallback(
    (
      eventName: MixpanelEventName,
      properties?: Record<string, unknown>,
      options?: RequestOptions
    ) => {
      void mixpanel.track(
        eventName,
        {
          ...properties,
          funnelSlug: trackingConfig.funnelSlug,
          funnelName: trackingConfig.funnelName,
          ...(trackingConfig.programId && { programId: trackingConfig.programId }),
          marketplaceEventId,
          funnelExperiments: trackingConfig.funnelExperiments,
          // This new relation event identifies the actual submitted program.
          // Existing events retain their funnel-configured program attribution.
          ...(eventName === "Form Funnel - Enrollment Linked" &&
          typeof properties?.programId === "string" &&
          properties.programId
            ? { programId: properties.programId }
            : {}),
        },
        options
      );
    },
    [
      mixpanel,
      trackingConfig.funnelSlug,
      trackingConfig.funnelName,
      trackingConfig.programId,
      trackingConfig.funnelExperiments,
      marketplaceEventId,
    ]
  );

  return (
    <FormFunnelTrackingContext.Provider
      value={{
        trackStepComplete,
        trackFormSubmit,
        trackFlowStep,
        trackHostedFlowStep,
        getTrackingIds,
        trackEvent,
      }}
    >
      <TrustedFormScript
        customerId={customerId || "unknown"}
        enabled={trackingConfig.enableTrustedForm ?? false}
      />
      {children}
    </FormFunnelTrackingContext.Provider>
  );
};
